import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock } from "lucide-react";
import { API } from "../utils/helpers";

const storageKey = (profile) => `qen_access_${profile}`;

const AccessGate = ({ profile, children }) => {
  // default/ana sayfa kilitli degil
  const locked = profile && profile !== "default";
  const [status, setStatus] = useState(locked ? "checking" : "ok");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getKey = () => {
    try { return localStorage.getItem(storageKey(profile)) || ""; } catch { return ""; }
  };

  const checkStatus = useCallback(async () => {
    const device_key = getKey();
    if (!device_key) { setStatus("locked"); return; }
    try {
      const res = await axios.post(`${API}/access/status`, { profile, device_key });
      setStatus(res.data?.authorized ? "ok" : "locked");
    } catch {
      // Ag hatasinda, daha once yetkiliyse kilitleme (TV ekranlari kesintide calismaya devam etsin)
      setStatus((s) => (s === "ok" ? "ok" : "locked"));
    }
  }, [profile]);

  useEffect(() => {
    if (!locked) return;
    checkStatus();
  }, [locked, checkStatus]);

  // Admin cihazi kaldirinca: arka planda surekli sorgu YOK.
  // Sadece sayfa yeniden gorunur oldugunda (sekmeye donus) tekrar kontrol edilir.
  useEffect(() => {
    if (!locked) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") checkStatus();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [locked, checkStatus]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const c = code.trim().toUpperCase();
    if (c.length !== 6) { setError("6 haneli kodu girin."); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/access/redeem`, { profile, code: c });
      if (res.data?.success) {
        try { localStorage.setItem(storageKey(profile), res.data.device_key); } catch {}
        setStatus("ok");
      } else {
        setError(res.data?.error || "Kod gecersiz.");
      }
    } catch {
      setError("Baglanti hatasi. Tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!locked || status === "ok") return children;

  if (status === "checking") {
    return (
      <div style={S.wrap} data-testid="access-checking">
        <p style={{ color: "#DA9F54", fontSize: 15, fontWeight: 600 }}>Kontrol ediliyor...</p>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <form onSubmit={submit} style={S.box} data-testid="access-lock">
        <div style={S.iconWrap}><Lock size={30} color="#DA9F54" /></div>
        <h1 style={S.title}>QEN ÖZBEK KUYUMCULUK</h1>
        <p style={S.sub}>Bu sayfa kilitli. Lütfen doğrulama kodunuzu girin.</p>
        <input
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          maxLength={6}
          placeholder="______"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          style={S.input}
          data-testid="access-code-input"
          autoFocus
        />
        <button type="submit" style={{ ...S.btn, opacity: submitting ? 0.6 : 1 }} disabled={submitting} data-testid="access-submit">
          {submitting ? "Kontrol ediliyor..." : "Giriş Yap"}
        </button>
        {error && <p style={S.err} data-testid="access-error">{error}</p>}
      </form>
    </div>
  );
};

const S = {
  wrap: {
    position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
    background: "radial-gradient(circle at 50% 30%, #13241c 0%, #0a1510 60%, #060d09 100%)",
    padding: 20, zIndex: 30000,
  },
  box: {
    width: "100%", maxWidth: 360, background: "rgba(16, 32, 25, 0.9)",
    border: "1px solid #2c3a33", borderRadius: 18, padding: "34px 26px",
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  },
  iconWrap: {
    width: 64, height: 64, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(218,159,84,0.12)", border: "1px solid rgba(218,159,84,0.4)", marginBottom: 16,
  },
  title: { color: "#DA9F54", fontSize: 18, fontWeight: 800, letterSpacing: 1.5, margin: "0 0 6px" },
  sub: { color: "#9fb3a8", fontSize: 13, margin: "0 0 22px", lineHeight: 1.5 },
  input: {
    width: "100%", boxSizing: "border-box", padding: "14px 10px", fontSize: 28, letterSpacing: 12,
    textAlign: "center", textTransform: "uppercase", fontWeight: 700,
    background: "#0c1812", color: "#fff", border: "1px solid #2c3a33", borderRadius: 12, outline: "none",
    marginBottom: 16,
  },
  btn: {
    width: "100%", padding: "14px", fontSize: 16, fontWeight: 700, color: "#0a1510",
    background: "#DA9F54", border: "none", borderRadius: 12, cursor: "pointer",
  },
  err: { color: "#f87171", fontSize: 13, margin: "14px 0 0" },
};

export default AccessGate;
